import { Dialog } from '@headlessui/react';
import { useState, useEffect } from 'react';
import { BlogPost, CreatePostInput, UpdatePostInput, FileMetadata } from '../../models/Post';
import { X } from 'lucide-react';
import { lazy, Suspense } from 'react';
import type { IAllProps } from '@tinymce/tinymce-react';
import { fileService } from '../../services/fileService';


const Editor = lazy(() => 
  import('@tinymce/tinymce-react')
    .then(({ Editor }) => ({ default: (props: IAllProps) => <Editor {...props} /> }))
);

interface PostModalProps {
    isOpen: boolean;
    onClose: () => void;
    post?: BlogPost | null;
    onSubmit: (post: CreatePostInput | UpdatePostInput) => Promise<void>;
    isCreating: boolean;
}

interface PostFormData {
    Title: string;
    Excerpt: string;
    Content: string;
    ImageUrl: string;
    Tags: string[];
    Status: 'draft' | 'published';
    Author?: {
        Id: string;
        Name: string;
    };
    DownloadableFile?: FileMetadata;
}

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const DEFAULT_EDITOR_CONFIG = {
    height: 500,
    min_height: 300,
    menubar: false,
    plugins: [
        'anchor', 'autolink', 'charmap', 'codesample', 'emoticons',
        'image', 'link', 'lists', 'media', 'searchreplace',
        'table', 'visualblocks', 'wordcount', 'fullscreen'
    ],
    toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat | fullscreen',
    content_style: `
        body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            font-size: 14px;
            margin: 0;
            padding: 1rem;
        }
        .tox-tinymce {
            border: 1px solid #d9d9d9 !important;
            border-radius: 6px !important;
        }
        .tox .tox-toolbar__group {
            padding: 0 3px !important;
            border: none !important;
        }
        .tox .tox-toolbar__primary {
            background: none !important;
            border: none !important;
        }
        .tox .tox-tbtn {
            height: 28px !important;
            width: 28px !important;
            margin: 0 1px !important;
            padding: 2px !important;
        }
        .tox .tox-tbtn--select {
            width: auto !important;
            padding: 2px 4px !important;
        }
        .tox .tox-tbtn svg {
            width: 16px !important;
            height: 16px !important;
        }
        .tox .tox-tbtn[aria-haspopup="true"] {
            width: auto !important;
            padding-right: 8px !important;
        }
        .tox .tox-toolbar__group:first-of-type {
            margin-left: 0 !important;
        }
        .tox .tox-toolbar-overlord {
            background: none !important;
        }
        .tox .tox-toolbar__primary {
            background: none !important;
            border-bottom: 1px solid #d9d9d9 !important;
        }
        .tox .tox-edit-area {
            border-top: none !important;
        }
        .tox.tox-tinymce-fullscreen {
            z-index: 1100 !important;
        }
    `,
    skin: 'oxide',
    statusbar: true,
    resize: true,
    branding: false,
    elementpath: false,
    promotion: false,
    browser_spellcheck: true,
    fullscreen_native: true,
    automatic_uploads: true
};

export function EditBlogPostModal({ isOpen, onClose, post, onSubmit, isCreating }: PostModalProps) {
    const [formData, setFormData] = useState<PostFormData>({
        Title: '',
        Excerpt: '',
        Content: '',
        ImageUrl: 'https://picsum.photos/200/300',
        Tags: [],
        Status: 'draft',
        Author: isCreating ? {
            Id: '1',
            Name: 'Admin'
        } : undefined
    });

    const [tagInput, setTagInput] = useState('');
    const [uploadingFile, setUploadingFile] = useState(false);

    useEffect(() => {
        if (post) {
            setFormData({
                Title: post.Title,
                Excerpt: post.Excerpt,
                Content: post.Content,
                ImageUrl: post.ImageUrl,
                Tags: post.Tags,
                Status: typeof post.Status === 'number' 
                    ? post.Status === 0 ? 'draft' : 'published'
                    : post.Status || 'draft',
                Author: post.Author
            });
        }
    }, [post]);

    const addTag = (newTag: string) => {
        const trimmedTag = newTag.trim();
        if (trimmedTag && !formData.Tags.includes(trimmedTag)) {
            setFormData(prev => ({
                ...prev,
                Tags: [...prev.Tags, trimmedTag]
            }));
        }
        setTagInput('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            const submitData = {
                ...formData,
                Status: formData.Status === 'draft' ? 0 : 1,
                Id: isCreating ? undefined : post?.Id
            };
                
            console.log('Submitting form with data:', submitData);
            await onSubmit(isCreating ? submitData as CreatePostInput : submitData as UpdatePostInput);
            console.log('Form submission successful');
            onClose();
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        
        // Only handle comma as separator during typing
        if (input.endsWith(',')) {
            const newTag = input.slice(0, -1); // Remove the comma
            addTag(newTag);
        } else {
            setTagInput(input);
        }
    };

    const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent form submission
            addTag(tagInput);
        }
    };

    const handleTagDelete = (indexToDelete: number) => {
        setFormData(prev => ({
            ...prev,
            Tags: prev.Tags.filter((_, index) => index !== indexToDelete)
        }));
    };

    const handleEditorChange = (content: string) => {
        setFormData(prev => ({
            ...prev,
            Content: content
        }));
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            console.log('Starting file upload:', {
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type
            });
            
            setUploadingFile(true);
            const response = await fileService.uploadFile(file);
            console.log('File upload successful, received metadata:', response);
            
            // Extract the actual file data from the response
            const fileMetadata = response;
            
            setFormData(prev => {
                console.log('Updating form data with new file metadata:', {
                    currentFileMetadata: prev.DownloadableFile,
                    newFileMetadata: fileMetadata
                });
                
                return {
                    ...prev,
                    DownloadableFile: fileMetadata  // Use the extracted data
                };
            });
        } catch (error) {
            console.error('Failed to upload file:', error);
        } finally {
            setUploadingFile(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
            
            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-start justify-center p-4">
                    <Dialog.Panel className="relative w-full max-w-2xl bg-white rounded-xl shadow-lg my-4">
                        <div className="sticky top-0 z-10 flex justify-between items-center p-6 border-b bg-white rounded-t-xl">
                            <Dialog.Title className="text-xl font-semibold">
                                {post ? 'Edit Post' : 'Create New Post'}
                            </Dialog.Title>
                            <button 
                                onClick={onClose}
                                className="hover:bg-gray-100 p-2 rounded-full transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div>
                            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Title</label>
                                    <input
                                        type="text"
                                        value={formData.Title}
                                        onChange={e => setFormData(prev => ({ ...prev, Title: e.target.value }))}
                                        className="w-full p-2 border rounded"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Excerpt</label>
                                    <textarea
                                        value={formData.Excerpt}
                                        onChange={e => setFormData(prev => ({ ...prev, Excerpt: e.target.value }))}
                                        className="w-full p-2 border rounded h-20"
                                        placeholder="Brief summary of the post..."
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Content</label>
                                    <Suspense fallback={<div className="h-[500px] animate-pulse bg-gray-100 rounded-lg"></div>}>
                                        {isOpen && <Editor
                                            apiKey="zy7jbm5u57aoqt2qowtza73u6ua8eliyf4yeeywj0mmjnvq5"
                                            value={formData.Content}
                                            onEditorChange={handleEditorChange}
                                            init={DEFAULT_EDITOR_CONFIG}
                                        />}
                                    </Suspense>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Image URL</label>
                                    <input
                                        type="url"
                                        value={formData.ImageUrl}
                                        onChange={e => setFormData(prev => ({ ...prev, ImageUrl: e.target.value }))}
                                        className="w-full p-2 border rounded"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Tags</label>
                                    <input
                                        type="text"
                                        value={tagInput}
                                        onChange={handleTagsChange}
                                        onKeyDown={handleTagKeyDown}
                                        className="w-full p-2 border rounded"
                                        placeholder="Type a tag and press comma or enter"
                                    />
                                    {formData.Tags.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {formData.Tags.map((tag, index) => (
                                                <span 
                                                    key={index}
                                                    className="bg-gray-100 px-2 py-1 rounded-full text-sm flex items-center gap-1"
                                                >
                                                    {tag}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleTagDelete(index)}
                                                        className="hover:text-red-500 ml-1"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Status</label>
                                    <select
                                        value={formData.Status}
                                        onChange={e => setFormData(prev => ({ 
                                            ...prev, 
                                            Status: e.target.value as 'draft' | 'published'
                                        }))}
                                        className="w-full p-2 border rounded"
                                    >
                                        <option value="draft">Draft</option>
                                        <option value="published">Published</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Downloadable Resource
                                    </label>
                                    <div className="mt-1 space-y-2">
                                        <input
                                            type="file"
                                            onChange={handleFileUpload}
                                            accept=".pdf,.doc,.docx"
                                            className="block w-full text-sm text-gray-500
                                                file:mr-4 file:py-2 file:px-4
                                                file:rounded-md file:border-0
                                                file:text-sm file:font-semibold
                                                file:bg-primary file:text-white
                                                hover:file:bg-primary/90"
                                            disabled={uploadingFile}
                                            name="file"
                                        />
                                        {uploadingFile && (
                                            <div className="text-sm text-gray-500 flex items-center">
                                                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Uploading...
                                            </div>
                                        )}
                                        {formData.DownloadableFile && (
                                            <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                                                <div className="font-medium">Current file:</div>
                                                <div>{formData.DownloadableFile.FileName}</div>
                                                <div className="text-gray-500">
                                                    {formatFileSize(formData.DownloadableFile.FileSize)} • 
                                                    {formData.DownloadableFile.ContentType}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-4 mt-6">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
                                    >
                                        {post ? 'Update' : 'Create'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </Dialog.Panel>
                </div>
            </div>
        </Dialog>
    );
} 